import { useEffect, useRef, useState } from "react";
import { Music, SkipForward, Play, Pause } from "lucide-react";
import { useAssetCatalog } from "@/lib/queries";
import { useStickyState } from "@/lib/useStickyState";
import { setMusicDuckHandler, setAudioSession } from "@/lib/commentary";
import { cn } from "@/lib/cn";

// Background-music player for the Scorer. Plays the active "music"
// assets (admin-uploaded, R2) as a looping playlist, and DUCKS its own
// volume while the AI announcer is talking (via setMusicDuckHandler) —
// dips to ~22% during a line, restores after. Renders nothing when no
// music tracks are uploaded.
const DUCK = 0.22;

export function MusicController() {
  const { data: tracks = [] } = useAssetCatalog("music");
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const duckedRef = useRef(false);
  const [playing, setPlaying] = useState(false);
  const [idx, setIdx] = useState(0);
  const [vol, setVol] = useStickyState<number>("scorer.music.vol", 0.6);
  const [show, setShow] = useState(false);

  // One long-lived audio element.
  useEffect(() => {
    const a = new Audio();
    audioRef.current = a;
    return () => {
      a.pause();
      audioRef.current = null;
    };
  }, []);

  // Effective volume = base × (ducked ? DUCK : 1). Re-registers the duck
  // handler whenever the base volume changes so it dips from the latest.
  useEffect(() => {
    const apply = () => {
      const a = audioRef.current;
      if (a) {
        a.volume =
          Math.max(0, Math.min(1, vol)) * (duckedRef.current ? DUCK : 1);
      }
    };
    apply();
    setMusicDuckHandler((ducked) => {
      duckedRef.current = ducked;
      apply();
    });
    return () => setMusicDuckHandler(null);
  }, [vol]);

  // Load + play/pause the current track.
  useEffect(() => {
    const a = audioRef.current;
    const track = tracks[idx];
    if (!a || !track?.imageUrl) return;
    if (a.src !== track.imageUrl) a.src = track.imageUrl;
    if (playing) {
      setAudioSession("playback"); // route to Bluetooth media, not earpiece
      a.play().catch(() => setPlaying(false));
    } else a.pause();
  }, [idx, playing, tracks]);

  // Advance to the next track when one finishes (loop the playlist).
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onEnded = () =>
      setIdx((i) => (tracks.length ? (i + 1) % tracks.length : 0));
    a.addEventListener("ended", onEnded);
    return () => a.removeEventListener("ended", onEnded);
  }, [tracks.length]);

  if (tracks.length === 0) return null;
  const current = tracks[idx];

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setShow((v) => !v)}
        aria-label="Background music"
        className={cn(
          "flex flex-col items-center justify-center gap-0.5 px-4 py-2 min-w-[4.75rem]",
          playing ? "text-primary" : "text-muted-foreground hover:text-foreground",
        )}
      >
        <Music size={22} />
        <span className="text-[10px] font-display uppercase tracking-wider">
          Music
        </span>
      </button>
      {show && (
        <div className="absolute left-0 top-full mt-1 z-40 w-60 rounded-md border border-border bg-card shadow-lg p-3 space-y-3 font-sans normal-case tracking-normal text-left">
          <div className="text-[10px] font-display uppercase tracking-wider text-muted-foreground">
            Now playing
          </div>
          <div className="text-xs font-medium truncate">
            {current?.name ?? "—"}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPlaying((p) => !p)}
              aria-label={playing ? "Pause" : "Play"}
              className="w-8 h-8 shrink-0 inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground"
            >
              {playing ? <Pause size={14} /> : <Play size={14} />}
            </button>
            <button
              type="button"
              onClick={() => setIdx((i) => (i + 1) % tracks.length)}
              aria-label="Next track"
              className="w-8 h-8 shrink-0 inline-flex items-center justify-center rounded-full border border-border text-muted-foreground hover:text-foreground"
            >
              <SkipForward size={14} />
            </button>
            <input
              type="range"
              min={0}
              max={100}
              value={Math.round(vol * 100)}
              onChange={(e) => setVol(Number(e.target.value) / 100)}
              className="flex-1 accent-primary"
              aria-label="Music volume"
            />
          </div>
          <p className="text-[10px] text-muted-foreground">
            Dips automatically while the announcer is talking.
          </p>
        </div>
      )}
    </div>
  );
}
